/* =========================================================
   Cart drawer
   - Intercepts any form that POSTs to /cart/add
   - Slides the drawer in, renders fresh cart state
   - Handles qty changes + remove inline
   - Updates header cart count bubble
   ========================================================= */

(function () {
  'use strict';

  const MONEY_FORMAT = (window.Shopify && Shopify.money_format) || '${{amount}}';
  const ROOT = '/';
  const DRAWER_SEL = '[data-cart-drawer]';
  const BODY_SEL = '[data-cart-drawer-body]';
  const FOOT_SEL = '[data-cart-drawer-foot]';
  const SUBTOTAL_SEL = '[data-cart-drawer-subtotal]';

  let lastFocus = null;

  function formatMoney(cents) {
    // Shopify stores prices as integer cents.
    const amount = (cents / 100).toFixed(2);
    if (MONEY_FORMAT.includes('{{amount}}')) return MONEY_FORMAT.replace('{{amount}}', amount);
    if (MONEY_FORMAT.includes('{{amount_no_decimals}}')) return MONEY_FORMAT.replace('{{amount_no_decimals}}', Math.round(cents / 100));
    return '$' + amount;
  }

  function escape(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // Flatten every applied discount into { title, amount } rows: cart-level
  // (order discounts like store credit) plus line-level allocations grouped by
  // title (product discounts like the preorder % and trade-in).
  function collectDiscounts(cart) {
    const out = [];
    (cart.cart_level_discount_applications || []).forEach((d) => {
      const amt = Number(d.total_allocated_amount || 0);
      if (amt > 0) out.push({ title: d.title || d.code || 'Discount', amount: amt });
    });
    const byTitle = {};
    (cart.items || []).forEach((it) => {
      (it.line_level_discount_allocations || []).forEach((a) => {
        const t = (a.discount_application && (a.discount_application.title || a.discount_application.code)) || 'Discount';
        byTitle[t] = (byTitle[t] || 0) + Number(a.amount || 0);
      });
    });
    Object.keys(byTitle).forEach((t) => { if (byTitle[t] > 0) out.push({ title: t, amount: byTitle[t] }); });
    return out;
  }

  function drawer() { return document.querySelector(DRAWER_SEL); }

  function openDrawer() {
    const d = drawer();
    if (!d) return;
    lastFocus = document.activeElement;
    d.hidden = false;
    // Force reflow so the transition plays
    // eslint-disable-next-line no-unused-expressions
    d.offsetHeight;
    d.setAttribute('aria-hidden', 'false');
    d.classList.add('is-open');
    document.body.classList.add('cart-drawer-open');
    const panel = d.querySelector('.cart-drawer__panel');
    if (panel) panel.focus();
  }

  function closeDrawer() {
    const d = drawer();
    if (!d) return;
    d.classList.remove('is-open');
    d.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-drawer-open');
    // Wait for transition before hiding so screen readers don't see stale state
    setTimeout(() => {
      if (!d.classList.contains('is-open')) d.hidden = true;
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    }, 300);
  }

  function renderItems(cart) {
    const body = document.querySelector(BODY_SEL);
    const foot = document.querySelector(FOOT_SEL);
    const subtotal = document.querySelector(SUBTOTAL_SEL);
    if (!body) return;

    if (!cart || cart.item_count === 0) {
      body.innerHTML = `
        <div class="cart-drawer__empty" data-cart-drawer-empty>
          <div class="cart-drawer__empty-ic" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" width="32" height="32">
              <path d="M6 7h14l-1.5 10a2 2 0 0 1-2 1.7H9.5A2 2 0 0 1 7.5 17L6 7z"/>
              <path d="M9 7V5a3 3 0 0 1 6 0v2"/>
            </svg>
          </div>
          <h3>Your cart is empty</h3>
          <p>Looks like you haven't added anything yet.</p>
          <a href="${ROOT}" class="mf-btn mf-btn--primary" data-cart-drawer-close>
            <span>Continue shopping</span><span class="arrow" aria-hidden="true">→</span>
          </a>
        </div>`;
      if (foot) foot.hidden = true;
      return;
    }

    if (foot) foot.hidden = false;
    if (subtotal) subtotal.textContent = formatMoney(cart.total_price);

    // Itemize every applied discount (preorder %, trade-in, store credit, …)
    // rather than lumping them into one row.
    const discountsEl = document.querySelector('[data-cart-drawer-discounts]');
    if (discountsEl) {
      discountsEl.innerHTML = collectDiscounts(cart).map((d) =>
        '<div class="cart-drawer__subtotal cart-drawer__subtotal--discount">' +
        '<span>' + escape(d.title) + '</span>' +
        '<strong>− ' + formatMoney(d.amount) + '</strong></div>'
      ).join('');
    }

    const itemsHtml = cart.items.map((item) => {
      const img = item.image
        ? `<img src="${item.image.replace(/(\.\w+)(\?.*)?$/, '_160x$1$2')}" alt="${escape(item.product_title)}" width="80" height="80" loading="lazy">`
        : '';
      const hasOptions = item.variant_title && item.variant_title !== 'Default Title';
      const optionRows = hasOptions && Array.isArray(item.options_with_values) && item.options_with_values.length
        ? item.options_with_values.map((o) =>
            `<div class="cart-drawer__variant"><span class="cart-drawer__variant-label">${escape(o.name)}:</span> ${escape(o.value)}</div>`
          ).join('')
        : (hasOptions ? `<div class="cart-drawer__variant">${escape(item.variant_title)}</div>` : '');
      const variant = optionRows;

      const lineAllocs = item.line_level_discount_allocations || [];
      const hasLineDiscount = Number(item.original_price || 0) > Number(item.final_price || 0);
      const priceBlock = hasLineDiscount
        ? `<div class="cart-drawer__price-row">
             <span class="cart-drawer__price">${formatMoney(item.final_price)}</span>
             <span class="cart-drawer__price-was">${formatMoney(item.original_price)}</span>
           </div>
           ${lineAllocs.map((a) => {
             const title = (a.discount_application && a.discount_application.title) || 'Discount';
             return `<span class="cart-drawer__discount-pill">${escape(title)} (−${formatMoney(a.amount)})</span>`;
           }).join('')}`
        : `<div class="cart-drawer__price">${formatMoney(item.final_price)}</div>`;

      return `
        <li class="cart-drawer__item" data-cart-item-key="${escape(item.key)}" data-variant-id="${escape(item.variant_id)}">
          <a href="${escape(item.url)}" class="cart-drawer__thumb">${img}</a>
          <div class="cart-drawer__meta">
            <a href="${escape(item.url)}" class="cart-drawer__name">${escape(item.product_title)}</a>
            ${variant}
            ${priceBlock}
            <div class="cart-drawer__qty">
              <button type="button" class="qty-btn" data-cart-qty="decrement" aria-label="Decrease quantity">−</button>
              <input type="number" class="qty-input" value="${item.quantity}" min="0" data-cart-qty-input aria-label="Quantity">
              <button type="button" class="qty-btn" data-cart-qty="increment" aria-label="Increase quantity">+</button>
              <button type="button" class="cart-drawer__remove" data-cart-remove aria-label="Remove">Remove</button>
            </div>
          </div>
          <div class="cart-drawer__line-total">${formatMoney(item.final_line_price)}</div>
        </li>`;
    }).join('');

    body.innerHTML = `<ul class="cart-drawer__items" data-cart-drawer-items>${itemsHtml}</ul>`;
  }

  function updateHeaderCount(count) {
    document.querySelectorAll('[data-cart-count], .cart-count').forEach((el) => {
      if (count && count > 0) {
        el.textContent = count;
        el.hidden = false;
        el.style.display = '';
      } else {
        el.textContent = '';
        el.hidden = true;
        el.style.display = 'none';
      }
    });
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('cart fetch failed');
    return res.json();
  }

  async function refreshCart() {
    try {
      const cart = await fetchCart();
      renderItems(cart);
      updateHeaderCount(cart.item_count);
      // If the standalone /cart page is open, re-render it too
      const pageCart = document.querySelector('[data-cart-page]');
      if (pageCart && typeof window.renderFullCart === 'function') {
        window.renderFullCart(cart);
      }
      return cart;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[cart-drawer] refresh failed', e);
      return null;
    }
  }

  async function addToCartViaForm(form) {
    const submit = form.querySelector('[type="submit"]');
    const origHtml = submit ? submit.innerHTML : '';
    if (submit) {
      submit.disabled = true;
      submit.classList.add('is-loading');
    }

    try {
      const fd = new FormData(form);
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || err.message || 'Could not add to cart');
      }
      await refreshCart();
      openDrawer();
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(err.message || 'Sorry — we couldn\'t add that to your cart.');
    } finally {
      if (submit) {
        submit.disabled = false;
        submit.classList.remove('is-loading');
        submit.innerHTML = origHtml;
      }
    }
  }

  async function changeLine(key, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity }),
    });
    if (!res.ok) throw new Error('cart change failed');
    const cart = await res.json();
    renderItems(cart);
    updateHeaderCount(cart.item_count);
    const pageCart = document.querySelector('[data-cart-page]');
    if (pageCart && typeof window.renderFullCart === 'function') {
      window.renderFullCart(cart);
    }
    return cart;
  }

  function bindGlobalListeners() {
    // Intercept all add-to-cart form submissions
    document.addEventListener('submit', (e) => {
      const form = e.target.closest('form[action*="/cart/add"]');
      if (!form || form.dataset.cartSkip === 'true') return;
      e.preventDefault();
      addToCartViaForm(form);
    });

    // Cart icon clicks open the drawer
    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-cart-open]');
      if (opener) {
        e.preventDefault();
        openDrawer();
        return;
      }

      const closer = e.target.closest('[data-cart-drawer-close]');
      if (closer) {
        e.preventDefault();
        closeDrawer();
        return;
      }

      // Quantity +/- buttons
      const qtyBtn = e.target.closest('[data-cart-qty]');
      if (qtyBtn) {
        e.preventDefault();
        const li = qtyBtn.closest('[data-cart-item-key]');
        if (!li) return;
        const input = li.querySelector('[data-cart-qty-input]');
        if (!input) return;
        const dir = qtyBtn.dataset.cartQty === 'increment' ? 1 : -1;
        const next = Math.max(0, parseInt(input.value || '0', 10) + dir);
        input.value = next;
        changeLine(li.dataset.cartItemKey, next);
        return;
      }

      // Remove link
      const rm = e.target.closest('[data-cart-remove]');
      if (rm) {
        e.preventDefault();
        const li = rm.closest('[data-cart-item-key]');
        if (!li) return;
        changeLine(li.dataset.cartItemKey, 0);
      }
    });

    // Quantity input change
    document.addEventListener('change', (e) => {
      const input = e.target.closest('[data-cart-qty-input]');
      if (!input) return;
      const li = input.closest('[data-cart-item-key]');
      if (!li) return;
      const next = Math.max(0, parseInt(input.value || '0', 10));
      changeLine(li.dataset.cartItemKey, next);
    });

    // Escape closes drawer
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        const d = drawer();
        if (d && d.classList.contains('is-open')) closeDrawer();
      }
    });
  }

  // Expose a small API for other scripts (e.g. the full cart page)
  window.MindCart = {
    open: openDrawer,
    close: closeDrawer,
    refresh: refreshCart,
    change: changeLine,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindGlobalListeners);
  } else {
    bindGlobalListeners();
  }
})();
